import Box from '@/Atoms/Box';
import CCButton from '@/Atoms/CCButton';
import CCText from '@/Atoms/CCText';
import SlideModal from '@/Atoms/SlideModal';
import Spacer from '@/Atoms/Spacer';
import useClassroomJoin from '@/Hooks/ClassroomInfoApi/useClassroomJoin';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {ScreenName} from '@/navigators/StackConstants';
import {ActiveMeetingsModal} from '@/Screens/Books/components/ActiveMeetingsModal';
import {PopUp} from '@/Screens/Visualisation/components/LevelModal';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useRestrictClassModalStore from '@/Store/RestrictClassModalStore';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {useNavigation} from '@react-navigation/native';
import React, {useState} from 'react';
import {
  Image,
  Linking,
  Modal,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import MaterialCommunityIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import {csocSetupStyles as styles} from './styles';

export type CsocSetupDashboard = {
  show: boolean;
  order_id: string;
  paid_on?: string;
  amount_paid?: number;
  currency?: string;
  invoice_url?: string;
  format?: 'group' | 'personal' | 'webinar' | string;
  level?: string;
  total_classes?: number;
  coach_name?: string | null;
  batch_name?: string | null;
  batch_code?: string | null;
  first_class_at?: string | null;
  first_class_link?: string | null;
  first_class?: {
    id: number;
    webinar_id?: string | null;
    occurence_id?: string | null;
    topic?: string | null;
    coach_name?: string | null;
    session_number?: number | null;
    starts_at: string;
    ends_at: string;
    duration_minutes?: number;
    class_type?: string | null;
    status: 'scheduled' | 'in_progress' | 'completed' | string;
    join_url?: string | null;
    registrant_token?: string | null;
    meeting_password?: string | null;
    meeting_role?: string | null;
    is_zoom_meeting?: number | null;
  } | null;
  whatsapp?: string | null;
  email?: string | null;
  profile_completed?: boolean;
  profile_completion?: {
    completed_fields: number;
    total_fields: number;
    percentage: number;
  };
  demo_video_url?: string;
  support_url?: string;
  curriculum?: Array<{
    session_number: number;
    topic: string;
  }>;
};

type Props = {
  setup: CsocSetupDashboard;
};

type SetupStep = {
  title: string;
  completeLabel: string;
  pendingLabel: string;
  done: boolean;
  icon: string;
};

const pendingText = 'Being finalized';
const CSOC_DEMO_VIDEO_ID = 14267;
const supportPhone = '+91 80 4735 8900';
const supportEmail = 'support@circlechess.com';
const supportWhatsappUrl =
  'https://api.whatsapp.com/send/?phone=918047358900&text=Hi&type=phone_number&app_absent=0';
const handbookPdfUrl =
  'https://cc-home.s3.ap-south-1.amazonaws.com/CircleChess_Welcome_Kit.docx.pdf#toolbar=0';

const openInNewTab = (url?: string) => {
  if (!url || typeof window === 'undefined') {
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const formatDate = (value?: string) => {
  if (!value) {
    return 'Confirmed';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatClassDate = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const getClassDateParts = (value?: string | null) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return {month: '', day: ''};
  }

  return {
    month: new Intl.DateTimeFormat('en-IN', {month: 'short'}).format(date),
    day: new Intl.DateTimeFormat('en-IN', {day: '2-digit'}).format(date),
  };
};

const isBeforeClassDay = (value?: string | null) => {
  const classDate = value ? new Date(value) : null;
  if (!classDate || Number.isNaN(classDate.getTime())) {
    return true;
  }

  const classDay = new Date(
    classDate.getFullYear(),
    classDate.getMonth(),
    classDate.getDate(),
  );
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return today.getTime() < classDay.getTime();
};

const formatClassTimeRange = (
  startsAt?: string | null,
  endsAt?: string | null,
) => {
  const start = startsAt ? new Date(startsAt) : null;
  const end = endsAt ? new Date(endsAt) : null;
  if (!start || Number.isNaN(start.getTime())) {
    return '';
  }

  const timeFormatter = new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const zoneFormatter = new Intl.DateTimeFormat('en-IN', {
    timeZoneName: 'short',
  });
  const zone = zoneFormatter
    .formatToParts(start)
    .find(part => part.type === 'timeZoneName')?.value;
  const endLabel =
    end && !Number.isNaN(end.getTime()) ? timeFormatter.format(end) : '';

  return [
    endLabel
      ? `${timeFormatter.format(start)} - ${endLabel}`
      : timeFormatter.format(start),
    zone,
  ]
    .filter(Boolean)
    .join(' ');
};

const formatMeetingStatus = (status?: string) => {
  if (!status) {
    return 'Scheduled';
  }
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatAmount = (amount?: number, currency = 'INR') => {
  if (typeof amount !== 'number') {
    return 'Confirmed';
  }

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount}`;
  }
};

const formatClassFormat = (value?: string) => {
  if (!value) {
    return pendingText;
  }
  const normalized = value.toLowerCase();
  if (normalized === 'group') {
    return 'Group classes';
  }
  if (normalized === 'personal') {
    return 'Personal classes';
  }
  if (normalized === 'webinar') {
    return 'Webinar';
  }
  return value;
};

function SectionHeading({
  icon,
  title,
  subtitle,
}: {
  icon: string;
  title: string;
  subtitle: string;
}) {
  return (
    <View style={styles.sectionHeading}>
      <View style={styles.sectionIcon}>
        <MaterialCommunityIcon name={icon} size={20} color="#55ADA4" />
      </View>
      <View style={styles.sectionHeadingCopy}>
        <CCText style={styles.sectionTitle}>{title}</CCText>
        <CCText style={styles.sectionSubtitle}>{subtitle}</CCText>
      </View>
    </View>
  );
}

function DetailRow({
  label,
  value,
  pending,
  last,
}: {
  label: string;
  value: React.ReactNode;
  pending?: boolean;
  last?: boolean;
}) {
  return (
    <View style={[styles.detailRow, last && styles.detailRowLast]}>
      <CCText style={styles.detailLabel}>{label}</CCText>
      <CCText style={[styles.detailValue, pending && styles.pendingValue]}>
        {value}
      </CCText>
    </View>
  );
}

function InvoiceRow({url}: {url?: string}) {
  return (
    <View style={[styles.detailRow, styles.detailRowLast]}>
      <CCText style={styles.detailLabel}>Invoice</CCText>
      {url ? (
        <TouchableOpacity
          accessibilityRole="link"
          onPress={() => openInNewTab(url)}
          style={styles.invoiceAction}>
          <MaterialCommunityIcon
            name="download-outline"
            size={17}
            color="#4D4038"
          />
          <CCText style={styles.invoiceActionText}>Download invoice</CCText>
        </TouchableOpacity>
      ) : (
        <CCText style={[styles.detailValue, styles.pendingValue]}>
          Unavailable
        </CCText>
      )}
    </View>
  );
}

function SetupMilestones({
  steps,
  compact,
}: {
  steps: SetupStep[];
  compact: boolean;
}) {
  const doneCount = steps.filter(step => step.done).length;
  const nextStep = steps.findIndex(step => !step.done);

  return (
    <View
      style={[
        styles.card,
        compact && styles.cardCompact,
        compact ? styles.milestoneCardCompact : styles.milestoneCard,
      ]}>
      <SectionHeading
        icon="check-decagram-outline"
        title="Account milestones"
        subtitle={`${doneCount} of ${steps.length} setup steps are ready.`}
      />
      <View style={styles.milestoneList}>
        <View style={styles.milestoneTrack} />
        {steps.map((step, index) => {
          const current = index === nextStep;
          return (
            <View key={step.title} style={styles.milestoneRow}>
              <View
                style={[
                  styles.milestoneDot,
                  step.done && styles.milestoneDotDone,
                  current && styles.milestoneDotCurrent,
                ]}>
                <MaterialCommunityIcon
                  name={step.done ? 'check' : step.icon}
                  size={18}
                  color={
                    step.done ? CCColors.White : current ? '#B48C15' : '#99938F'
                  }
                />
              </View>
              <View style={styles.milestoneCopy}>
                <CCText style={styles.milestoneTitle}>{step.title}</CCText>
                <CCText
                  style={[
                    styles.milestoneStatus,
                    step.done && styles.milestoneStatusDone,
                  ]}>
                  {step.done ? step.completeLabel : step.pendingLabel}
                </CCText>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
}

function WalkthroughPrompt({onPress}: {onPress: () => void}) {
  return (
    <View style={styles.walkthroughPrompt}>
      <View style={styles.walkthroughTopRow}>
        <View style={styles.walkthroughIcon}>
          <MaterialCommunityIcon
            name="play-circle-outline"
            size={27}
            color="#4D9F96"
          />
        </View>
        <View style={styles.walkthroughCopy}>
          <CCText style={styles.walkthroughTitle}>
            Prepare with the Caissa walkthrough
          </CCText>
          <CCText style={styles.walkthroughDescription}>
            Learn where to find classes and enter the classroom.
          </CCText>
        </View>
      </View>
      <CCButton
        onPress={onPress}
        style={styles.walkthroughButton}
        contentContainerStyle={styles.walkthroughButtonContent}>
        <MaterialCommunityIcon name="play" size={17} color="#302A27" />
        <Spacer spacing={7} horizontal />
        <CCText fontSize={FontSizes[14]} fontWeight="bold">
          View walkthrough
        </CCText>
      </CCButton>
    </View>
  );
}

function SupportContact({
  icon,
  label,
  value,
  onPress,
}: {
  icon: string;
  label: string;
  value: string;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      accessibilityRole="link"
      onPress={onPress}
      style={styles.supportContact}>
      <View style={styles.supportContactIcon}>
        <MaterialCommunityIcon name={icon} size={20} color="#55ADA4" />
      </View>
      <View style={styles.supportContactCopy}>
        <CCText style={styles.supportContactLabel}>{label}</CCText>
        <CCText style={styles.supportContactValue}>{value}</CCText>
      </View>
      <MaterialCommunityIcon name="open-in-new" size={17} color="#8D8884" />
    </TouchableOpacity>
  );
}

function SupportModal({
  visible,
  onClose,
  onRaiseTicket,
}: {
  visible: boolean;
  onClose: () => void;
  onRaiseTicket: () => void;
}) {
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}>
      <View style={styles.modalBackdrop}>
        <View style={styles.supportModal}>
          <View style={styles.supportModalHeader}>
            <View style={styles.supportModalHeaderCopy}>
              <CCText style={styles.supportModalTitle}>
                Contact CircleChess support
              </CCText>
              <CCText style={styles.supportModalSubtitle}>
                Choose a direct contact method or raise a tracked ticket.
              </CCText>
            </View>
            <TouchableOpacity
              accessibilityLabel="Close support"
              onPress={onClose}
              style={styles.modalCloseButton}>
              <MaterialCommunityIcon name="close" size={21} color="#4D433D" />
            </TouchableOpacity>
          </View>
          <SupportContact
            icon="phone-outline"
            label="Call us"
            value={supportPhone}
            onPress={() => Linking.openURL('tel:+918047358900')}
          />
          <SupportContact
            icon="email-outline"
            label="Email us"
            value={supportEmail}
            onPress={() => Linking.openURL(`mailto:${supportEmail}`)}
          />
          <SupportContact
            icon="whatsapp"
            label="WhatsApp channel"
            value="Start a WhatsApp chat"
            onPress={() => Linking.openURL(supportWhatsappUrl)}
          />
          <View style={styles.supportModalActions}>
            <CCButton
              type="outline"
              onPress={onClose}
              style={styles.modalDoneButton}
              contentContainerStyle={styles.modalButtonContent}>
              <CCText fontSize={FontSizes[14]} fontWeight="bold">
                Done
              </CCText>
            </CCButton>
            <CCButton
              onPress={onRaiseTicket}
              style={styles.modalTicketButton}
              contentContainerStyle={styles.modalButtonContent}>
              <MaterialCommunityIcon
                name="ticket-confirmation-outline"
                size={18}
                color="#302A27"
              />
              <Spacer spacing={7} horizontal />
              <CCText fontSize={FontSizes[14]} fontWeight="bold">
                Raise a ticket
              </CCText>
            </CCButton>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function PracticeCard({
  title,
  subtitle,
  image,
  backgroundColor,
  onPress,
  isMobile,
}: {
  title: string;
  subtitle: string;
  image: any;
  backgroundColor: string;
  onPress: () => void;
  isMobile: boolean;
}) {
  return (
    <TouchableOpacity
      accessibilityRole="button"
      onPress={onPress}
      style={[styles.practiceCard, isMobile && styles.practiceCardMobile]}>
      <View style={[styles.practiceVisual, {backgroundColor}]}>
        <Image
          source={image}
          style={styles.practiceImage}
          resizeMode="contain"
        />
      </View>
      <View style={styles.practiceCopy}>
        <CCText style={styles.practiceTitle}>{title}</CCText>
        <CCText style={styles.practiceSubtitle}>{subtitle}</CCText>
      </View>
    </TouchableOpacity>
  );
}

export default function CsocPaidSetupDashboard({setup}: Props) {
  const navigation = useNavigation<any>();
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showHandbook, setShowHandbook] = useState(false);
  const userData = useLoggedInUserStore(state => state.userData);
  const {isMobileOrTab} = useMediaQuery();
  const {width: viewportWidth} = useWindowDimensions();
  const isCompactLayout = isMobileOrTab || viewportWidth <= 1100;
  const {
    showActiveMeetingsModal,
    activeMeetingsCount,
    activeLoading,
    handleJoinClick,
    handleModalClose,
    handleModalJoin,
    handleModalEndAndJoin,
  } = useClassroomJoin();
  const {
    showRestrictClassModal,
    setShowRestrictClassModal,
    showJoinStatusMessage,
  } = useRestrictClassModalStore();

  const firstClass = setup.first_class;
  const firstClassStartsAt = firstClass?.starts_at || setup.first_class_at;
  const firstClassEndsAt = firstClass?.ends_at;
  const firstClassJoinUrl = firstClass?.join_url || setup.first_class_link;
  const hasFirstClass = Boolean(
    firstClass || firstClassStartsAt || firstClassJoinUrl,
  );
  const hasCoach = Boolean(setup.coach_name);
  const hasBatch = Boolean(setup.batch_name || setup.batch_code);
  const firstClassDate = formatClassDate(firstClassStartsAt);
  const firstClassTimeRange = formatClassTimeRange(
    firstClassStartsAt,
    firstClassEndsAt,
  );
  const firstClassDateParts = getClassDateParts(firstClassStartsAt);
  const firstClassCoach = firstClass?.coach_name || setup.coach_name;
  const firstClassStatus = formatMeetingStatus(firstClass?.status);
  const firstClassType =
    firstClass?.class_type ||
    (setup.format === 'personal' ? '1:1' : formatClassFormat(setup.format));
  const batchLabel =
    setup.batch_name &&
    setup.batch_code &&
    setup.batch_name !== setup.batch_code
      ? `${setup.batch_name} - ${setup.batch_code}`
      : setup.batch_name || setup.batch_code || pendingText;
  const profileCompletionPercentage = Math.min(
    100,
    Math.max(
      0,
      setup.profile_completion?.percentage ??
        (setup.profile_completed ? 100 : 0),
    ),
  );
  const profileProgressColor =
    profileCompletionPercentage >= 80
      ? '#5EADA4'
      : profileCompletionPercentage >= 50
      ? '#D5A936'
      : '#D87850';
  const profileProgressWidth =
    `${profileCompletionPercentage}%` as `${number}%`;
  const displayName = userData?.first_name || userData?.username;
  const canJoinClassroom = Boolean(
    firstClass?.webinar_id &&
      firstClass?.occurence_id &&
      firstClass?.registrant_token,
  );
  const showWalkthrough = isBeforeClassDay(firstClassStartsAt);
  const curriculumCount = setup.curriculum?.length || setup.total_classes || 0;

  const steps: SetupStep[] = [
    {
      title: 'Payment',
      completeLabel: 'Confirmed',
      pendingLabel: 'Up next',
      done: true,
      icon: 'credit-card-check-outline',
    },
    {
      title: 'Account',
      completeLabel: 'Created',
      pendingLabel: 'Up next',
      done: true,
      icon: 'account-check-outline',
    },
    {
      title: 'Batch',
      completeLabel: 'Assigned',
      pendingLabel: 'In progress',
      done: hasBatch,
      icon: 'progress-clock',
    },
    {
      title: 'Coach',
      completeLabel: 'Assigned',
      pendingLabel: 'In progress',
      done: hasCoach,
      icon: 'progress-clock',
    },
    {
      title: 'Schedule',
      completeLabel: 'Confirmed',
      pendingLabel: 'In progress',
      done: Boolean(firstClassStartsAt),
      icon: 'progress-clock',
    },
    {
      title: 'First class',
      completeLabel: 'Completed',
      pendingLabel:
        firstClass?.status === 'in_progress' ? 'In progress' : 'Up next',
      done: false,
      icon: 'calendar-blank-outline',
    },
  ];

  const completeProfile = () => navigation.navigate(ScreenName.Settings);
  const openWalkthrough = () => {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(
      'ZoomUserData',
      JSON.stringify({
        meetingId: '95691',
        occurenceId: '176694',
        meetingRole: 'student',
        videoId: CSOC_DEMO_VIDEO_ID,
        batchType: setup.format || '',
        topic: 'Caissa walkthrough',
        recording_available: true,
        source: 'recording',
      }),
    );
    openInNewTab(`${window.location.origin}/${ScreenName.Classroom}`);
  };
  const openSubscription = () =>
    navigation.navigate(ScreenName.Books, {tab: 'subscription'});
  const joinFirstClass = () => {
    if (!firstClass || !canJoinClassroom) {
      return;
    }

    handleJoinClick({
      id: firstClass.id,
      user_id: userData?.user_id,
      webinar_id: firstClass.webinar_id || '',
      registrant_token: firstClass.registrant_token || '',
      meeting_password: firstClass.meeting_password || '',
      occurence_id: firstClass.occurence_id || '',
      meeting_role: firstClass.meeting_role || 'student',
      is_zoom_meeting: firstClass.is_zoom_meeting ?? 1,
      batch_type: setup.format || '',
      class_type: firstClass.class_type || '',
      webinar_topic: firstClass.topic || 'First class',
      join_url: firstClass.join_url || '',
    });
  };
  const raiseSupportTicket = () => {
    setShowSupportModal(false);
    navigation.navigate(ScreenName.SupportTicket);
  };

  return (
    <Box style={[styles.page, isCompactLayout && styles.pageMobile]}>
      <View style={styles.content}>
        <View
          style={[
            styles.paymentStrip,
            isCompactLayout && styles.paymentStripMobile,
          ]}>
          <View style={styles.paymentStripMessage}>
            <View style={styles.paymentStripIcon}>
              <MaterialCommunityIcon
                name="check"
                size={15}
                color={CCColors.White}
              />
            </View>
            <CCText style={styles.paymentStripTitle}>Payment confirmed</CCText>
            <CCText style={styles.paymentStripText}>
              Your CircleChess enrollment is active.
            </CCText>
          </View>
          <View style={styles.paymentStripMeta}>
            <CCText style={styles.paymentStripMetaText}>
              Order {setup.order_id || 'Confirmed'}
            </CCText>
            <CCText style={styles.paymentStripMetaText}>|</CCText>
            <CCText style={styles.paymentStripMetaText}>
              Paid {formatDate(setup.paid_on)}
            </CCText>
          </View>
        </View>

        <View style={[styles.welcome, isCompactLayout && styles.welcomeMobile]}>
          <View style={styles.welcomeCopy}>
            <CCText style={styles.welcomeEyebrow}>
              CIRCLECHESS SCHOOL OF CHESS
            </CCText>
            <CCText style={styles.welcomeTitle}>
              {displayName
                ? `Welcome, ${displayName}.`
                : 'Welcome to CircleChess.'}
            </CCText>
            <CCText style={styles.welcomeBody}>
              We are preparing your coach, schedule, and first class. Everything
              you need for your enrollment is tracked below.
            </CCText>
            <CCButton
              type="outline"
              onPress={() => setShowSupportModal(true)}
              style={styles.contactSupportButton}
              contentContainerStyle={styles.contactSupportButtonContent}>
              <MaterialCommunityIcon
                name="message-outline"
                size={17}
                color={CCColors.White}
              />
              <Spacer spacing={7} horizontal />
              <CCText
                fontSize={FontSizes[13]}
                fontWeight="bold"
                color={CCColors.White}>
                Contact support
              </CCText>
            </CCButton>
          </View>

          <View
            style={[
              styles.welcomeProfile,
              isCompactLayout && styles.welcomeProfileMobile,
            ]}>
            <View style={styles.welcomeProfileHeading}>
              <CCText style={styles.welcomeProfileTitle}>
                {profileCompletionPercentage === 100
                  ? 'Your learning profile is complete'
                  : 'Complete your learning profile'}
              </CCText>
              <CCText
                style={[
                  styles.welcomeProfilePercentage,
                  {color: profileProgressColor},
                ]}>
                {profileCompletionPercentage}%
              </CCText>
            </View>
            <View style={styles.welcomeProgressTrack}>
              <View
                style={[
                  styles.welcomeProgressFill,
                  {
                    width: profileProgressWidth,
                    backgroundColor: profileProgressColor,
                  },
                ]}
              />
            </View>
            <View style={styles.welcomeProfileFooter}>
              <CCText style={styles.welcomeProfileMeta}>
                {setup.profile_completion
                  ? `${setup.profile_completion.completed_fields} of ${setup.profile_completion.total_fields} fields completed`
                  : profileCompletionPercentage === 100
                  ? 'All profile fields completed'
                  : 'Add your learning preferences'}
              </CCText>
              {profileCompletionPercentage < 100 && (
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={completeProfile}>
                  <CCText style={styles.continueProfileText}>
                    Continue profile {'->'}
                  </CCText>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {!isCompactLayout && (
            <Image
              source={require('@/Assets/Images/Caissa/Caissa_bot_girl.svg')}
              style={styles.welcomeCaissa}
              resizeMode="contain"
            />
          )}
        </View>

        <View
          style={[styles.setupGrid, isCompactLayout && styles.setupGridMobile]}>
          <View
            style={[
              styles.card,
              isCompactLayout && styles.cardCompact,
              isCompactLayout
                ? styles.learningCardCompact
                : styles.learningCard,
            ]}>
            <View style={styles.learningHeadingRow}>
              <SectionHeading
                icon="chess-knight"
                title="Your learning plan"
                subtitle="Your first class and paid curriculum together."
              />
              <CCButton
                type="outline"
                onPress={() => setShowHandbook(true)}
                style={styles.subscriptionKnowMore}
                contentContainerStyle={styles.subscriptionKnowMoreContent}>
                <MaterialCommunityIcon
                  name="file-pdf-box"
                  size={15}
                  color="#4D4038"
                />
                <Spacer spacing={5} horizontal />
                <CCText style={styles.subscriptionKnowMoreText}>
                  Platform Guide
                </CCText>
              </CCButton>
            </View>

            <View
              style={[
                styles.learningColumns,
                isCompactLayout && styles.learningColumnsMobile,
              ]}>
              <View
                style={[
                  styles.firstClassPanel,
                  isCompactLayout && styles.firstClassPanelMobile,
                ]}>
                <View style={styles.classState}>
                  <View style={styles.classDateTile}>
                    <View style={styles.classDateTileHeader}>
                      <CCText style={styles.classDateTileHeaderText}>
                        FIRST CLASS
                      </CCText>
                    </View>
                    <CCText style={styles.classDateTileDay}>
                      {firstClassDateParts.day || '--'}
                    </CCText>
                    {firstClassDateParts.month ? (
                      <CCText style={styles.classDateTileMonth}>
                        {firstClassDateParts.month}
                      </CCText>
                    ) : null}
                  </View>
                  <View style={styles.classStateCopy}>
                    <CCText style={styles.classStateLabel}>
                      {hasFirstClass
                        ? firstClassStatus.toUpperCase()
                        : 'SCHEDULING IN PROGRESS'}
                    </CCText>
                    <CCText style={styles.classStateTitle}>
                      {hasFirstClass
                        ? firstClass?.topic || 'Your first class'
                        : 'Date and time coming soon'}
                    </CCText>
                    <CCText style={styles.classStateDescription}>
                      {hasFirstClass
                        ? [firstClassDate, firstClassCoach]
                            .filter(Boolean)
                            .join(' | ')
                        : 'Classroom details will appear here once confirmed.'}
                    </CCText>
                  </View>
                </View>

                {hasFirstClass && (
                  <View style={styles.classMeta}>
                    <View style={styles.classMetaItem}>
                      <MaterialCommunityIcon
                        name="clock-outline"
                        size={17}
                        color="#77706B"
                      />
                      <CCText style={styles.classMetaText}>
                        {firstClassTimeRange || firstClassDate}
                      </CCText>
                    </View>
                    <View style={styles.classMetaItem}>
                      <MaterialCommunityIcon
                        name="monitor"
                        size={17}
                        color="#77706B"
                      />
                      <CCText style={styles.classMetaText}>
                        {firstClassType}
                      </CCText>
                    </View>
                  </View>
                )}

                {showWalkthrough ? (
                  <WalkthroughPrompt onPress={openWalkthrough} />
                ) : canJoinClassroom ? (
                  <CCButton
                    onPress={joinFirstClass}
                    disabled={activeLoading}
                    style={styles.joinButton}
                    contentContainerStyle={styles.joinButtonContent}>
                    <CCText fontSize={FontSizes[14]} fontWeight="bold">
                      {activeLoading ? 'Opening classroom...' : 'Join class'}
                    </CCText>
                  </CCButton>
                ) : null}
              </View>

              <View
                style={[
                  styles.curriculumPanel,
                  isCompactLayout && styles.curriculumPanelMobile,
                ]}>
                <View
                  style={[
                    styles.curriculumHeader,
                    isCompactLayout && styles.curriculumHeaderMobile,
                  ]}>
                  <CCText style={styles.curriculumTitle}>
                    {setup.level
                      ? `${setup.level} curriculum`
                      : 'Your curriculum'}
                  </CCText>
                  <CCText style={styles.curriculumCount}>
                    {curriculumCount
                      ? `${curriculumCount} sessions - Scroll to explore`
                      : 'Curriculum pending'}
                  </CCText>
                </View>
                {setup.curriculum?.length ? (
                  <ScrollView
                    nestedScrollEnabled
                    style={styles.curriculumList}
                    contentContainerStyle={styles.curriculumListContent}>
                    {setup.curriculum.map(item => (
                      <View
                        key={`${item.session_number}-${item.topic}`}
                        style={styles.curriculumRow}>
                        <View style={styles.curriculumSessionBadge}>
                          <CCText style={styles.curriculumSessionNumber}>
                            {item.session_number}
                          </CCText>
                        </View>
                        <View style={styles.curriculumTopicCopy}>
                          <CCText style={styles.curriculumSessionLabel}>
                            Session {item.session_number}
                          </CCText>
                          <CCText style={styles.curriculumTopic}>
                            {item.topic}
                          </CCText>
                        </View>
                      </View>
                    ))}
                  </ScrollView>
                ) : (
                  <View style={styles.curriculumEmpty}>
                    <MaterialCommunityIcon
                      name="book-clock-outline"
                      size={24}
                      color="#8D8884"
                    />
                    <CCText style={styles.curriculumEmptyText}>
                      Curriculum details will appear here once available.
                    </CCText>
                  </View>
                )}
              </View>
            </View>
          </View>

          <SetupMilestones steps={steps} compact={isCompactLayout} />
        </View>

        <View style={[styles.card, styles.enrollmentCard]}>
          <View style={styles.enrollmentHeader}>
            <SectionHeading
              icon="table-large"
              title="Enrollment summary"
              subtitle="Payment and subscription details in one place."
            />
            <View style={styles.paidPill}>
              <MaterialCommunityIcon name="check" size={14} color="#25745F" />
              <CCText style={styles.paidPillText}>Paid</CCText>
            </View>
          </View>
          <View
            style={[
              styles.enrollmentColumns,
              isCompactLayout && styles.enrollmentColumnsMobile,
            ]}>
            <View
              style={[
                styles.enrollmentColumn,
                styles.paymentColumn,
                isCompactLayout && styles.enrollmentColumnMobile,
              ]}>
              <CCText style={styles.enrollmentColumnTitle}>Payment</CCText>
              <View
                style={[
                  styles.detailGrid,
                  isCompactLayout && styles.detailGridMobile,
                ]}>
                <View style={styles.detailGridColumn}>
                  <DetailRow
                    label="Order ID"
                    value={setup.order_id || 'Confirmed'}
                  />
                  <DetailRow
                    label="Paid on"
                    value={formatDate(setup.paid_on)}
                    last
                  />
                </View>
                <View style={styles.detailGridColumn}>
                  <DetailRow
                    label="Amount"
                    value={formatAmount(setup.amount_paid, setup.currency)}
                  />
                  <InvoiceRow url={setup.invoice_url} />
                </View>
              </View>
            </View>

            <View
              style={[
                styles.enrollmentColumn,
                styles.subscriptionColumn,
                isCompactLayout && styles.subscriptionColumnMobile,
              ]}>
              <View style={styles.enrollmentColumnHeader}>
                <CCText style={styles.enrollmentColumnTitle}>
                  Subscription
                </CCText>
                <CCButton
                  type="outline"
                  onPress={openSubscription}
                  style={styles.subscriptionKnowMore}
                  contentContainerStyle={styles.subscriptionKnowMoreContent}>
                  <CCText style={styles.subscriptionKnowMoreText}>
                    Know more
                  </CCText>
                  <Spacer spacing={5} horizontal />
                  <MaterialCommunityIcon
                    name="arrow-right"
                    size={15}
                    color="#4D4038"
                  />
                </CCButton>
              </View>
              <View
                style={[
                  styles.detailGrid,
                  isCompactLayout && styles.detailGridMobile,
                ]}>
                <View style={styles.detailGridColumn}>
                  <DetailRow
                    label="Format"
                    value={formatClassFormat(setup.format)}
                    pending={!setup.format}
                  />
                  <DetailRow
                    label="Classes"
                    value={
                      setup.total_classes
                        ? `${setup.total_classes} live classes`
                        : pendingText
                    }
                    pending={!setup.total_classes}
                  />
                  <DetailRow
                    label="Batch"
                    value={batchLabel}
                    pending={!hasBatch}
                    last
                  />
                </View>
                <View style={styles.detailGridColumn}>
                  <DetailRow
                    label="Level"
                    value={setup.level || pendingText}
                    pending={!setup.level}
                  />
                  <DetailRow
                    label="Coach"
                    value={setup.coach_name || pendingText}
                    pending={!setup.coach_name}
                  />
                  <DetailRow
                    label="Schedule"
                    value={
                      firstClassStartsAt
                        ? formatClassDate(firstClassStartsAt)
                        : pendingText
                    }
                    pending={!firstClassStartsAt}
                    last
                  />
                </View>
              </View>
            </View>
          </View>
        </View>

        <View
          style={[
            styles.practiceHeader,
            isCompactLayout && styles.practiceHeaderMobile,
          ]}>
          <CCText style={styles.practiceHeaderTitle}>
            Practice while we finish your setup
          </CCText>
          <CCText
            style={[
              styles.practiceHeaderSubtitle,
              isCompactLayout && styles.practiceHeaderSubtitleMobile,
            ]}>
            These activities do not affect your class setup.
          </CCText>
        </View>
        <View style={styles.practiceGrid}>
          <PracticeCard
            title="Play a game"
            subtitle="Find an online opponent."
            image={require('@/Assets/Images/Svgs/home_page/game_play.svg')}
            backgroundColor="#FFF1C7"
            onPress={() => navigation.navigate(ScreenName.PlayChess)}
            isMobile={isCompactLayout}
          />
          <PracticeCard
            title="Solve positions"
            subtitle="Practice tactical patterns."
            image={require('@/Assets/Images/Svgs/home_page/positions_trainer.svg')}
            backgroundColor="#E9F3FF"
            onPress={() => navigation.navigate(ScreenName.Puzzles)}
            isMobile={isCompactLayout}
          />
          <PracticeCard
            title="Calculation trainer"
            subtitle="Improve visualization depth."
            image={require('@/Assets/Images/Svgs/home_page/calculation_trainer.svg')}
            backgroundColor="#F0EBFF"
            onPress={() =>
              navigation.navigate(ScreenName.Visualisation, {
                selected: 'calculation',
              })
            }
            isMobile={isCompactLayout}
          />
          <PracticeCard
            title="Fun challenges"
            subtitle="Try puzzles and mini-games."
            image={require('@/Assets/Images/Svgs/home_page/intuition_trainer.svg')}
            backgroundColor="#FFF1C7"
            onPress={() => navigation.navigate(ScreenName.FunChallengesLobby)}
            isMobile={isCompactLayout}
          />
        </View>
      </View>

      <SupportModal
        visible={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        onRaiseTicket={raiseSupportTicket}
      />
      <SlideModal
        visible={showHandbook}
        type={isCompactLayout ? 'bottom' : 'center'}
        onClose={() => setShowHandbook(false)}
        modalHeaderTitle={'Platform Guide'}
        modalHeaderRight={
          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            onPress={() => openInNewTab(handbookPdfUrl)}>
            <MaterialCommunityIcon
              name="download"
              size={24}
              color={CCColors.Black}
            />
          </Box>
        }
        customStyle={{width: '90%', height: '90%', padding: 0}}>
        <View
          style={{
            flex: 1,
            backgroundColor: CCColors.White,
            borderRadius: 8,
            overflow: 'hidden',
          }}>
          <div style={{flex: 1, width: '100%', height: '100%'}}>
            <iframe
              src={handbookPdfUrl}
              style={{width: '100%', height: '100%', border: 'none'}}
              title="Platform Guide"
            />
          </div>
        </View>
      </SlideModal>
      {showRestrictClassModal && (
        <SlideModal
          visible={showRestrictClassModal}
          type="center"
          customStyle={styles.joinRestrictionModal}>
          <PopUp
            confirm={() => setShowRestrictClassModal(false)}
            text={showJoinStatusMessage}
            buttonConfirm="Okay"
          />
        </SlideModal>
      )}
      <ActiveMeetingsModal
        visible={showActiveMeetingsModal}
        activeMeetingsCount={activeMeetingsCount || 0}
        onClose={handleModalClose}
        onJoin={handleModalJoin}
        onEndAndJoin={handleModalEndAndJoin}
      />
    </Box>
  );
}
